import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CwWineRatingRecord, fetchWineRatings } from '../services/fetchWineDetail';
import { RowRatings } from '../ForYouChrome/rowQueries';

const HALF_HOUR = 30 * 60 * 1000;

// ────────────────────────────────────────────────────────────────────────────
// Batched ratings — fetches CW Ratings for all visible offers' lwin11s in one POST.
// Used by the For You hub rows so filter gates use real data instead of synthetic.
// ────────────────────────────────────────────────────────────────────────────

function toRowRatings(r: CwWineRatingRecord): RowRatings {
  return {
    vintage: r.vintageScore ?? 0,
    critic: r.normalizedCriticScore ?? r.combinedCriticScore ?? 0,
    fairValue: r.fairValueScore ?? 0,
    liquidity: r.liquidityScore ?? 0,
    performance: r.performanceScore ?? 0,
    grade: r.grade ?? 0,
  };
}

export function useBatchedRatings(lwin11s: string[]): {
  data: Map<string, RowRatings>;
  isLoading: boolean;
} {
  const sorted = useMemo(() => Array.from(new Set(lwin11s.filter(Boolean))).sort(), [lwin11s]);

  const query = useQuery({
    queryKey: ['batchedWineRatings', sorted],
    queryFn: () => fetchWineRatings(sorted),
    enabled: sorted.length > 0,
    staleTime: HALF_HOUR,
    cacheTime: HALF_HOUR * 2,
    refetchOnWindowFocus: false,
  });

  const data = useMemo(() => {
    const map = new Map<string, RowRatings>();
    (query.data ?? []).forEach((r) => {
      if (r.lwin11) map.set(r.lwin11, toRowRatings(r));
    });
    return map;
  }, [query.data]);

  return { data, isLoading: query.isLoading };
}
